import { every, isEmpty } from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import BankFields from '../BankFields/BankFields';
import TwoFactorAuth from '../TwoFactorAuth/TwoFactorAuth';
import {
  isValidAccountNumber,
  isValidRoutingNumber,
} from '../../Lib/Utils/FormUtils/vendor-application';
import AddressFields from '../AddressFields/AddressFields';
import {
  makeAddressNames,
  makeAddressValues,
} from '../../Lib/Utils/FormUtils/address-fields';
import isDomestic from '../../Lib/Utils/VendorApplication/is-domestic';

const useStyles = makeStyles()(() => ({
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '30px',
  },
}));

export default function BankFieldsUpdate(props) {
  const { classes } = useStyles();

  const [bankDetails, setBankDetails] = useState(props.values);
  const [authenticated, setAuthenticated] = useState(false);
  const domestic = isDomestic(bankDetails.payment_country);
  const [updates, setUpdates] = useState({});

  const validBankingDetails = every([
    !isEmpty(bankDetails.account_name),
    !isEmpty(bankDetails.bank_name),
    isValidAccountNumber(bankDetails.account_number, domestic),
    isValidRoutingNumber(bankDetails.routing_number, domestic),
    !isEmpty(bankDetails.payment_country),
    !isEmpty(bankDetails.payment_billing_address),
    !isEmpty(bankDetails.payment_city),
    !isEmpty(bankDetails.payment_zip),
    !isEmpty(bankDetails.reEnterAccountNumber) &&
      bankDetails.account_number === bankDetails.reEnterAccountNumber,
    !isEmpty(bankDetails.reEnterRoutingNumber) &&
      bankDetails.routing_number === bankDetails.reEnterRoutingNumber,
    domestic ? !isEmpty(bankDetails.payment_state) : true,
  ]);

  const onChange = (name, value) => {
    setBankDetails((d) => ({
      ...d,
      [name]: value,
    }));

    setUpdates((updated) => ({ ...updated, [name]: value }));
  };

  const onClose = () => {
    setAuthenticated(false);
    props.onClose();
  };

  return (
    <Dialog open={props.open} onClose={onClose}>
      <DialogTitle>Update Banking Info</DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          {!authenticated
            ? 'In order to update banking details, please authenticate first:'
            : 'Please update the information below:'}
        </Typography>

        {!authenticated && (
          <TwoFactorAuth
            onAuthenticated={() => setAuthenticated(true)}
            vendor={props.vendor}
          />
        )}

        {authenticated && (
          <div>
            <BankFields
              values={bankDetails}
              names={props.names}
              disabled={false}
              prefix=""
              emptyFields={props.emptyFields}
              domestic={domestic}
              onBlur={(e) => onChange(e.target.name, e.target.value)}
              onChange={onChange}
              header="Payment Info"
            />
            <AddressFields
              values={makeAddressValues(bankDetails, true)}
              names={makeAddressNames(true)}
              prefix="va-payment"
              domestic={domestic}
              onChange={onChange}
              onBlur={(e) => onChange(e.target.name, e.target.value)}
              emptyFields={props.emptyFields}
              required
              disabled={false}
            />
            <div className={classes.actions}>
              <Button
                color="primary"
                variant="contained"
                size="small"
                onClick={() => {
                  props.onSave(updates);
                }}
                disabled={!validBankingDetails}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

BankFieldsUpdate.propTypes = {
  values: PropTypes.shape({
    bank_name: PropTypes.string,
    account_name: PropTypes.string,
    account_number: PropTypes.string,
    routing_number: PropTypes.string,
  }).isRequired,
  names: PropTypes.shape({
    bank_name_field: PropTypes.string,
    account_name_field: PropTypes.string,
    account_number_field: PropTypes.string,
    routing_number_field: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  emptyFields: PropTypes.shape({}).isRequired,
  vendor: PropTypes.shape({}).isRequired,
};
